import { useEditorState, type Editor } from "@tiptap/react";
import {
  Bold,
  Code,
  Columns3,
  Heading1,
  Heading2,
  Heading3,
  Italic,
  Link as LinkIcon,
  List,
  ListOrdered,
  Minus,
  Pilcrow,
  Quote,
  Rows3,
  Strikethrough,
  Table as TableIcon,
  Trash2,
  Underline as UnderlineIcon,
  type LucideIcon,
} from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Formatting controls for the analysis document.
 *
 * Everything here is also reachable from the "/" menu or a keyboard shortcut —
 * the toolbar exists because a fund partner writing an IC memo should not have
 * to learn either.
 */

interface EditorToolbarProps {
  editor: Editor;
  disabled?: boolean;
}

export function EditorToolbar({ editor, disabled }: EditorToolbarProps) {
  // Subscribed rather than read once: active marks change with every cursor
  // move, and a toolbar that lies about the current state is worse than none.
  const state = useEditorState({
    editor,
    selector: ({ editor: current }) => ({
      bold: current.isActive("bold"),
      italic: current.isActive("italic"),
      underline: current.isActive("underline"),
      strike: current.isActive("strike"),
      code: current.isActive("code"),
      link: current.isActive("link"),
      h1: current.isActive("heading", { level: 1 }),
      h2: current.isActive("heading", { level: 2 }),
      h3: current.isActive("heading", { level: 3 }),
      paragraph: current.isActive("paragraph"),
      bulletList: current.isActive("bulletList"),
      orderedList: current.isActive("orderedList"),
      blockquote: current.isActive("blockquote"),
      inTable: current.isActive("table"),
    }),
  });

  const setLink = () => {
    const previous = editor.getAttributes("link").href as string | undefined;
    const href = window.prompt("Link URL", previous ?? "https://");
    if (href === null) return;
    if (!href.trim()) {
      editor.chain().focus().unsetLink().run();
      return;
    }
    editor.chain().focus().setLink({ href: href.trim() }).run();
  };

  return (
    <div className="sticky top-0 z-10 flex flex-col gap-1 border-b border-border bg-card/95 px-3 py-1.5 backdrop-blur">
      <div className="flex flex-wrap items-center gap-0.5">
        <Tool
          icon={Bold}
          label="Bold"
          shortcut="⌘B"
          active={state.bold}
          disabled={disabled}
          onClick={() => editor.chain().focus().toggleBold().run()}
        />
        <Tool
          icon={Italic}
          label="Italic"
          shortcut="⌘I"
          active={state.italic}
          disabled={disabled}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        />
        <Tool
          icon={UnderlineIcon}
          label="Underline"
          shortcut="⌘U"
          active={state.underline}
          disabled={disabled}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
        />
        <Tool
          icon={Strikethrough}
          label="Strikethrough"
          active={state.strike}
          disabled={disabled}
          onClick={() => editor.chain().focus().toggleStrike().run()}
        />
        <Tool
          icon={Code}
          label="Inline code"
          active={state.code}
          disabled={disabled}
          onClick={() => editor.chain().focus().toggleCode().run()}
        />

        <Divider />

        <Tool
          icon={Pilcrow}
          label="Body text"
          active={state.paragraph}
          disabled={disabled}
          onClick={() => editor.chain().focus().setParagraph().run()}
        />
        <Tool
          icon={Heading1}
          label="Heading 1"
          active={state.h1}
          disabled={disabled}
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 1 }).run()
          }
        />
        <Tool
          icon={Heading2}
          label="Heading 2"
          active={state.h2}
          disabled={disabled}
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 2 }).run()
          }
        />
        <Tool
          icon={Heading3}
          label="Heading 3"
          active={state.h3}
          disabled={disabled}
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 3 }).run()
          }
        />

        <Divider />

        <Tool
          icon={List}
          label="Bulleted list"
          active={state.bulletList}
          disabled={disabled}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
        />
        <Tool
          icon={ListOrdered}
          label="Numbered list"
          active={state.orderedList}
          disabled={disabled}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
        />
        <Tool
          icon={Quote}
          label="Quote"
          active={state.blockquote}
          disabled={disabled}
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
        />
        <Tool
          icon={Minus}
          label="Divider"
          disabled={disabled}
          onClick={() => editor.chain().focus().setHorizontalRule().run()}
        />

        <Divider />

        <Tool
          icon={LinkIcon}
          label="Link"
          active={state.link}
          disabled={disabled}
          onClick={setLink}
        />
        <Tool
          icon={TableIcon}
          label="Insert table"
          disabled={disabled}
          onClick={() =>
            editor
              .chain()
              .focus()
              .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
              .run()
          }
        />
      </div>

      {/* Table controls only exist while the cursor is in a table — a row of
          permanently greyed-out buttons teaches nothing. */}
      {state.inTable && !disabled && (
        <div className="flex flex-wrap items-center gap-0.5 border-t border-border/60 pt-1">
          <span className="px-1 text-[11px] uppercase tracking-wide text-muted-foreground">
            Table
          </span>
          <Tool
            icon={Columns3}
            label="Add column"
            onClick={() => editor.chain().focus().addColumnAfter().run()}
          />
          <Tool
            icon={Columns3}
            label="Delete column"
            destructive
            onClick={() => editor.chain().focus().deleteColumn().run()}
          />
          <Tool
            icon={Rows3}
            label="Add row"
            onClick={() => editor.chain().focus().addRowAfter().run()}
          />
          <Tool
            icon={Rows3}
            label="Delete row"
            destructive
            onClick={() => editor.chain().focus().deleteRow().run()}
          />
          <Tool
            icon={Trash2}
            label="Delete table"
            destructive
            onClick={() => editor.chain().focus().deleteTable().run()}
          />
        </div>
      )}
    </div>
  );
}

const Divider = () => <span className="mx-1 h-4 w-px bg-border" />;

interface ToolProps {
  icon: LucideIcon;
  label: string;
  shortcut?: string;
  active?: boolean;
  disabled?: boolean;
  destructive?: boolean;
  onClick: () => void;
}

const Tool = ({
  icon: Icon,
  label,
  shortcut,
  active,
  disabled,
  destructive,
  onClick,
}: ToolProps) => (
  <button
    type="button"
    // Keeps the selection in the editor: without this the button steals focus
    // and the command applies to nothing.
    onMouseDown={(event) => event.preventDefault()}
    onClick={onClick}
    disabled={disabled}
    title={shortcut ? `${label} (${shortcut})` : label}
    aria-label={label}
    aria-pressed={active}
    className={cn(
      "rounded p-1.5 transition-colors disabled:cursor-not-allowed disabled:opacity-40",
      active
        ? "bg-accent text-accent-foreground"
        : destructive
          ? "text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
          : "text-muted-foreground hover:bg-muted hover:text-foreground"
    )}
  >
    <Icon className="h-3.5 w-3.5" />
  </button>
);
